import React, { useState, Fragment } from "react";
import styled, { css } from "styled-components";
import SampleLink from "../SampleLink";
import CompanyLink from "../CompanyLink";
import moment from "moment";
import Link from "../Link";
import { fetchJson } from "../../lib/Fetching";
import SampleView from "./SampleView";
import SampleCommentCounts from "./SampleCommentCounts";
import { Column as AColumn, HalfColumn, FullColumn } from "../common/layout/Grid";
import classNames from "classnames";

const Td = styled.td`
  font-size: 8pt;
`;
const CenterTd = styled.td`
  font-size: 8pt;
  text-align: center;
`;

const Status = styled.td`
  font-size: 8pt;
  text-align: center;
  color: white;
`;

const ReviewedStatus = styled(Status)`
  background-color: rgb(50, 202, 50) !important;
`;
const InvoiceStatus = styled(Status)`
  background-color: rgb(113, 213, 249) !important;
  color: white;
  text-transform: uppercase;
  font-weight: bold;
`;
const SampleChargeStatus = styled(Status)`
  background-color: ${(props) =>
    props.charge_status === 1 ? "rgb(50, 202,50)" : "rgb(200,100,100)"}!important;
  &:before {
    content: '${(props) => (props.charge_status === 1 ? "Invoice" : "Credit")}'
  }
`;
const AnalysisUnderwayStatus = styled(Status)`
  background-color: rgb(143, 143, 255) !important;
`;
const UnreceivedStatus = styled(Status)`
  background-color: rgb(150, 150, 150) !important;
`;
const SampleWithdrawnStatus = styled(Status)`
  background-color: rgb(255, 0, 0) !important;
  color: white;
`;

const NowrapTd = styled(Td)`
  white-space: nowrap;
  ${(props) =>
    props.empty &&
    css`
      background: repeating-linear-gradient(
        45deg,
        #fafafa,
        #fafafa 10px,
        #efefef 10px,
        #efefef 20px
      );
    `};
`;

const NowrapCenterTd = styled(NowrapTd)`
  text-align: center;
`;

const OverflowTd = styled.td`
  font-size: 8pt;
  max-width: 0;
  min-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OverflowTdDate = styled(OverflowTd)`
  min-width: 70px;
  text-align: center;
`;

const LongOverflowTd = styled(OverflowTd)`
  ${(props) =>
    props.nowrap
      ? css`
          white-space: nowrap;
          overflow: none;
          max-width: inherit;
          min-width: inherit;
        `
      : css`
          min-width: 200px;
        `};
`;

const CenterOverflowTd = styled(OverflowTd)`
  text-align: center;
`;

const FieldTd = styled.td`
  font-size: 8pt;
  ${(props) =>
    props.empty &&
    css`
      background: repeating-linear-gradient(
        45deg,
        #fafafa,
        #fafafa 10px,
        #efefef 10px,
        #efefef 20px
      );
    `};
`;
const ExtraTr = styled.tr`
  > td {
  }
  &:hover {
    > td {
      background-color: #f5f7fa;
    }
  }
`;

const Tr = styled.tr`
  cursor: pointer;
  > td {
    padding-top: 2px !important;
    padding-bottom: 2px !important;
  }
  ${(props) =>
    props.selected &&
    css`
      > td {
        background-color: #f9ffb0;
      }
    `};
  ${(props) =>
    props.highlight &&
    css`
      > td {
        background-color: #d1e1ff;
      }
    `};
  ${(props) =>
    props.failure_count > 0
      ? css`
          > td {
            background-color: #ffe3e3;
          }
          &:hover {
            > td {
              background-color: #ffeaea;
            }
          }
        `
      : props.warning_count > 0
        ? css`
            > td {
              background-color: #ffeecf;
            }
            &:hover {
              > td {
                background-color: #fff4d4;
              }
            }
          `
        : ""};
  ${(props) =>
    props.failure_count == 0 &&
    props.warning_count == 0 &&
    css`
      &:hover {
        > td {
          background-color: #fafafa;
        }
      }
    `};
`;

export default function SampleRow({
  sample: originalSample,
  highlight,
  selected,
  canSelect,
  selectSample,
  showLaboratorySampleStatus,
  columns,
  showScheduledStatus,
  showCompany,
  showBranch,
  showAttributes,
  clientView = false,
  ExtraCols,
  showContractedTechniques = false,
  showScheduleableTechniques = false,
  techniques,
  reloadSamples,
  viewingSampleCharge = false,
  invoiceId,
  expandSample = false,
  nowrapProduct = false,
}) {
  const [showSample, setShowSample] = useState(expandSample);
  const [initialSampleView, setInitialSampleView] = useState(clientView ? "summary" : null);
  const [sample, setSample] = useState(originalSample);

  const reloadSample = () => {
    var params = {
      include_contracted_techniques: showContractedTechniques,
      include_scheduling_information: showScheduleableTechniques,
      with_unallocated_sample_ledger_items: !invoiceId && viewingSampleCharge,
      invoice_id: invoiceId,
    };
    return fetchJson(
      "/lims_sample_reporting/samples/" + sample.identifier + ".json",
      params
    ).then((newSample) =>
      setSample({
        ...newSample,
        laboratory_sample: sample.laboratory_sample,
      })
    );
  };

  const highlightRow = highlight && highlight(sample);
  const formatTime = (date: Date): string =>
    date
      ? moment
          .utc(date)
          .local()
          .format("DD/MM/YY HH:mm")
      : "";
  const formatDate = (date: Date): string =>
    date
      ? moment
          .utc(date)
          .local()
          .format("DD/MM/YY")
      : "";
  const getSampleValue = (column: Column) => {
    const value = sample.values.filter((v) => v.column_id === column.id)[0];
    if (!value) return null;
    return value.value;
  };

  const countIf = (bool) => (bool == true ? 1 : 0);

  const columnsVisible =
    14 +
    countIf(showCompany) +
    countIf(showBranch) +
    countIf(showLaboratorySampleStatus) +
    columns.length +
    (showScheduledStatus ? 2 : 1);

  return (
    <Fragment>
      <Tr
        key={sample.identifier}
        selected={selected}
        highlight={highlightRow}
        failure_count={sample.pass_fail_count}
        warning_count={sample.warning_count}
        onClick={() => setShowSample(!showSample)}
      >
        <CenterTd onClick={(e) => e.stopPropagation()}>
          {canSelect && (
            <input
              type="checkbox"
              checked={selected}
              onClick={(e) => e.stopPropagation()}
              onChange={selectSample}
            />
          )}
        </CenterTd>
        {sample.withdrawn ? (
          <SampleWithdrawnStatus>
            <b>{I18n.t("sample_withdrawn")}</b>
          </SampleWithdrawnStatus>
        ) : viewingSampleCharge ? (
          <SampleChargeStatus charge_status={sample.charge_type} />
        ) : sample.invoiced && !clientView ? (
          <InvoiceStatus>{I18n.t("sample_status.invoice")}</InvoiceStatus>
        ) : sample.reviewed ? (
          <ReviewedStatus>
            <b>{I18n.t("sample_status.reviewed")}</b>
          </ReviewedStatus>
        ) : sample.received_at ? (
          <AnalysisUnderwayStatus>
            <b>{I18n.t("sample_status.preliminary")}</b>
          </AnalysisUnderwayStatus>
        ) : (
          <UnreceivedStatus>
            {sample.contract_reviewed && (
              <b>
                <i className="fa fa-check" />
                CR&nbsp;
              </b>
            )}
            <b>{I18n.t("sample_status.unreceived")}</b>
          </UnreceivedStatus>
        )}
        <NowrapCenterTd>
          <SampleCommentCounts
            {...sample}
            clientView={clientView}
            handleShowComments={() => {
              setShowSample(!showSample);
              setInitialSampleView("comments");
            }}
            handleShowRetests={() => {
              setShowSample(!showSample);
              setInitialSampleView("sample_retests");
            }}
          />
        </NowrapCenterTd>
        {showScheduledStatus && (
          <CenterTd style={{ color: sample.scheduled ? "green" : "red" }}>
            {sample.scheduled ? I18n.t("yes") : I18n.t("no")}
          </CenterTd>
        )}
        {ExtraCols && <ExtraCols sample={sample} />}
        {showCompany && (
          <OverflowTd>
            {(sample.require_prepay_authorisation) && (
              <span
                className="label label-warning mr-sm"
                data-toggle="tooltip"
                data-placement="top"
                title="Profroma"
              >
                P
              </span>
            )}
            <CompanyLink
              company={{ id: sample.company_id, name: sample.company_name }}
              disabled={clientView}
            />
          </OverflowTd>
        )}
        {showBranch && <OverflowTd>{sample.company_branch_name}</OverflowTd>}
        <CenterTd>
          <SampleLink sample={sample} disabled={clientView} />
        </CenterTd>
        <CenterTd>{formatDate(sample.requested_test_date)}</CenterTd>
        <CenterTd>
          {sample.requested_turnaround ||
          sample.lturnaround ||
          sample.requested_turnaround === 0 ||
          sample.lturnaround === 0 ? (
            <span>
              {sample.requested_turnaround} / {sample.lturnaround}
            </span>
          ) : (
            "N/A"
          )}
        </CenterTd>
        <CenterTd>{formatDate(sample.due_date)}</CenterTd>
        <NowrapCenterTd empty={!sample.contract_reviewed}>
          {sample.contract_reviewed && (
            <Link
              onClick={() => {
                setShowSample(!showSample);
                setInitialSampleView("available_results");
              }}
            >
              {I18n.t("show")}
            </Link>
          )}
        </NowrapCenterTd>
        <NowrapCenterTd empty={!sample.contract_reviewed}>
          {sample.contract_reviewed && (
            <Link
              onClick={() => {
                setShowSample(!showSample);
                setInitialSampleView("reports");
              }}
            >
              {I18n.t("show")}
            </Link>
          )}
        </NowrapCenterTd>
        <LongOverflowTd nowrap={nowrapProduct}>
          {sample.product_name || sample.matrix_name}
        </LongOverflowTd>
        <OverflowTdDate>{formatTime(sample.registered_at)}</OverflowTdDate>
        {showLaboratorySampleStatus && (
          <Fragment>
            <OverflowTdDate>{formatTime(sample.laboratory_sample.received_at)}</OverflowTdDate>
            <OverflowTdDate>{formatTime(sample.laboratory_sample.sent_at)}</OverflowTdDate>
          </Fragment>
        )}
        {!showLaboratorySampleStatus && (
          <OverflowTdDate>{formatTime(sample.received_at)}</OverflowTdDate>
        )}
        <OverflowTd>{sample.client_suite_name}</OverflowTd>
        <OverflowTd>{sample.client_specification_name}</OverflowTd>
        {showAttributes &&
          columns.map((column) => {
            const value = getSampleValue(column);
            return (
              <FieldTd key={column.id} empty={value === null}>
                {value}
              </FieldTd>
            );
          })}
        {showContractedTechniques &&
          techniques.map((t) => {
            const contractedTechnique = sample.contracted_techniques[t.id.toString()];
            return (
              <CenterTd
                key={t.id}
                className={sample.contract_reviewed ? "contract-reviewed-td" : null}
              >
                {contractedTechnique && <i className="fa fa-check">&nbsp; </i>}
                {contractedTechnique && <span>{contractedTechnique.testing_branch.name}</span>}
              </CenterTd>
            );
          })}
        {showScheduleableTechniques &&
          techniques.map((t) => {
            const schedulableTechnique = sample.schedulable_techniques[t.id.toString()];
            const ColumnComponent =
              schedulableTechnique && schedulableTechnique.length > 1
                ? HalfColumn
                : FullColumn;
            return (
              <CenterTd key={t.id}>
                {schedulableTechnique && (
                  <div className="schedule_container">
                    {schedulableTechnique.map((st) => (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        className={classNames({
                          schedule_item: true,
                          scheduled: st.scheduled,
                        })}
                      >
                        {!st.scheduled && <input type="checkbox" checked />}
                        {st.scheduled && <span>{st.test_count}</span>}
                        &nbsp;
                        {st.branch}
                      </div>
                    ))}
                  </div>
                )}
              </CenterTd>
            );
          })}
        <CenterTd>{viewingSampleCharge ? sample.ledger_item_price : sample.price}</CenterTd>
      </Tr>
      {showSample && (
        <ExtraTr>
          <td
            colSpan={techniques ? columnsVisible + techniques.length : columnsVisible}
            style={{ border: "2px solid #ddd", backgroundColor: "white" }}
          >
            <SampleView
              reloadSamples={reloadSamples}
              reloadSample={reloadSample}
              sample={sample}
              clientView={clientView}
              initialView={initialSampleView}
              onClose={() => setShowSample(false)}
            />
          </td>
        </ExtraTr>
      )}
    </Fragment>
  );
}
